import type { CimpressDocumentV2 } from '@mcp-artwork/cimdoc';
import axios, { HttpStatusCode } from 'axios';
import base64js from 'base64-js';
import pako from 'pako';
import { v4 as uuidv4 } from 'uuid';
import { getAuthHeaders } from '../Auth';
import config from '../config';
import { getUrl, REQUESTER } from '../tools';
import type { DesignPersonalizationContext } from '../types/dpcs';
import type { GenerateAssets } from './composeGenerateApi';
import type { ComposeAssets } from './compositeApi';
import { createCimdocWithImage } from './generateCimdocUrl';
import type { InspirationGenerateOptions, InspirationGenerateV4Filters } from './inspirationGenerateApi';
import { uploadJsonDocumentAndGetDocumentUrl } from './uploadJsonDocumentAndGetDocumentUrl';

const host = config.backendServiceUrl;

export const getResizeByDimensionsUrl = (
    documentUrl: string | null,
    width: string,
    height: string,
    minimumFontSize: string | undefined,
    apiKey: string,
    priority: string = 'quality',
    version: string = 'v3',
): string | null => {
    if (!documentUrl) {
        return null;
    }

    const basePath = `/api/${version}/adaptation:resize`;

    const params: Record<string, string | undefined> = {
        width,
        height,
        documentUrl,
        requester: REQUESTER,
        apiKey,
        minimumFontSize,
        priority,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export const getTransferCustomizationUrl = (
    sourceUrl: string | null,
    targetUrl: string | null,
    transferCustomerAssets: boolean,
    apiKey: string,
    useSmartTextTransfer = false,
    version = 'v3',
    fixLogoContrast = false,
    enableMulticolorLogoContrastFix?: boolean,
): string | null => {
    if (!sourceUrl || !targetUrl) {
        return null;
    }

    const basePath = `/api/${version}/preservation:transferCustomizations`;

    const params: Record<string, string | undefined> = {
        sourceDocumentUrl: getUrl(sourceUrl) ?? '',
        targetDocumentUrl: getUrl(targetUrl) ?? '',
        transferCustomerAssets: transferCustomerAssets?.toString(),
        useSmartTextTransfer: useSmartTextTransfer?.toString(),
        fixLogoContrast: fixLogoContrast ? fixLogoContrast.toString() : undefined,
        ...(enableMulticolorLogoContrastFix && { enableMulticolorLogoContrastFix: 'true' }),
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export const getAdjustForSubstrateUrl = (
    sourceUrl: string | null,
    targetSubstrateColor: string,
    minimumContrastRatio: string | number,
    apiKey: string,
    enableMulticolorLogoContrastFix?: boolean,
): string | null => {
    if (!sourceUrl || !targetSubstrateColor) {
        return null;
    }

    const basePath = '/api/v3/adaptation:adjustForSubstrate';

    const params: Record<string, string | undefined> = {
        targetSubstrateColor,
        documentUrl: getUrl(sourceUrl) ?? '',
        minimumContrastRatio: minimumContrastRatio.toString(),
        requester: REQUESTER,
        apiKey,
        ...(enableMulticolorLogoContrastFix && { enableMulticolorLogoContrastFix: 'true' }),
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export const getAdjustForSurfaceUrl = (
    source: string | null,
    surfaceSpecificationUrl: string,
    targetSubstrateColor: string,
    apiKey: string,
    version: 'v0' | 'v3' = 'v3',
    surfaceSpecification?: unknown,
): string | null => {
    if (!source || (!surfaceSpecificationUrl && !surfaceSpecification)) {
        return null;
    }

    const basePath = `/api/${version}/adaptation:adjustForSurface`;

    const params: Record<string, string | undefined> = {
        documentUrl: getUrl(source) ?? '',
        surfaceSpecificationUrl: surfaceSpecificationUrl || undefined,
        surfaceSpecification: surfaceSpecification ? JSON.stringify(surfaceSpecification) : undefined,
        targetSubstrateColor: targetSubstrateColor || undefined,
        requester: REQUESTER,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export async function getAdjustForSurfaceDocumentUrl(
    source: string | null,
    surfaceSpecificationUrl: string,
    targetSubstrateColor: string,
    apiKey: string,
    useV0: boolean,
    signal: AbortSignal,
    surfaceSpecification?: unknown,
): Promise<string> {
    const url = getAdjustForSurfaceUrl(
        source,
        surfaceSpecificationUrl,
        targetSubstrateColor,
        apiKey,
        useV0 ? 'v0' : 'v3',
        surfaceSpecification,
    );
    if (!url || !useV0) {
        return url ?? '';
    }

    // Rendering services cannot authenticate a document URL on the user's behalf.
    // Resolve v0 here, keeping the Bearer token only in the browser request headers.
    signal.throwIfAborted();
    const document = await getCimpressDocumentV2(url, signal);
    signal.throwIfAborted();
    try {
        return getTransientDocumentUrl(document);
    } catch {
        // Large documents exceed the transient URL limit; use the existing storage fallback.
        return uploadJsonDocumentAndGetDocumentUrl(document, signal, 1);
    }
}

export const getImproveArtworkContrastUrl = (
    sourceUrl: string | null,
    minimumContrastRatio: number | string | null,
    apiKey: string,
    fixLogoContrast = false,
    enableMulticolorLogoContrastFix?: boolean,
): string | null => {
    if (!sourceUrl) {
        return null;
    }

    const basePath = '/api/v3/quality:improveContrast';

    const params: Record<string, string | undefined> = {
        documentUrl: getUrl(sourceUrl) ?? '',
        minimumContrastRatio: minimumContrastRatio?.toString(),
        fixLogoContrast: fixLogoContrast ? fixLogoContrast.toString() : undefined,
        ...(enableMulticolorLogoContrastFix && { enableMulticolorLogoContrastFix: 'true' }),
        requester: REQUESTER,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export const getTransferStyleUrl = (
    sourceUrl: string | null,
    targetUrl: string | null,
    apiKey: string,
    fixLogoContrast = false,
    enableMulticolorLogoContrastFix?: boolean,
): string | null => {
    if (!sourceUrl || !targetUrl) {
        return null;
    }

    const basePath = '/api/v3/preservation:transferStyle';

    const params: Record<string, string | undefined> = {
        sourceDocumentUrl: getUrl(sourceUrl) ?? '',
        targetDocumentUrl: getUrl(targetUrl) ?? '',
        fixLogoContrast: fixLogoContrast ? fixLogoContrast.toString() : undefined,
        ...(enableMulticolorLogoContrastFix && { enableMulticolorLogoContrastFix: 'true' }),
        requester: REQUESTER,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export function getFontSwapUrl(
    documentUrl: string | undefined,
    sourceFont: string | undefined,
    targetFont: string | undefined,
    apiKey: string,
): string | null {
    if (!documentUrl || !sourceFont || !targetFont) {
        return null;
    }

    const basePath = '/api/v3/composition:swapFont';

    const params: Record<string, string | undefined> = {
        documentUrl: getUrl(documentUrl) ?? '',
        sourceFont,
        targetFont,
        requester: REQUESTER,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);
    return url.toString();
}

export const getComposeUrl = ({
    documentUrl,
    assets,
    apiKey,
    priority = 'quality',
    version = 'v3',
    hostUrl = host,
    endpoint,
    surfaceSpecificationUrl,
    surfaceSpecification,
    applyOnlyToPlaceholders = false,
    fillDefaultText = false,
    smartCrop = false,
    brandProfileUrl,
    featureFlags: {
        enableMulticolorLogoContrastFix = false,
        enableMultiColorLogoToMonochromeConversion = false,
        enableLogoOutlineContrastFix = false,
        enableMulticolorLogoContrastBackgroundAdjustment = false,
        // Omit these gallery experiments from existing callers' JSON unless explicitly supplied.
        enableMulticolorLogoContrastBackgroundAdjustmentImprovement,
        enableMulticolorLogoContrastBackgroundAdjustmentDarkShades,
    } = {},
    templateId,
    designPersonalizationContextId,
    dpcs,
    deliveryMode,
    culture,
    productId,
    productVersion,
    productOptions,
    sourceProductId,
    sourceProductVersion,
    sourceProductOptions,
    resize,
}: {
    documentUrl: string | null;
    assets: ComposeAssets | null;
    apiKey: string;
    priority: string;
    version?: string;
    hostUrl?: string;
    endpoint?: string;
    surfaceSpecificationUrl?: string;
    surfaceSpecification?: any;
    applyOnlyToPlaceholders?: boolean;
    fillDefaultText?: boolean;
    smartCrop?: boolean;
    brandProfileUrl?: string;
    featureFlags?: {
        enableMulticolorLogoContrastFix?: boolean;
        enableMultiColorLogoToMonochromeConversion?: boolean;
        enableLogoOutlineContrastFix?: boolean;
        enableMulticolorLogoContrastBackgroundAdjustment?: boolean;
        enableMulticolorLogoContrastBackgroundAdjustmentImprovement?: boolean;
        enableMulticolorLogoContrastBackgroundAdjustmentDarkShades?: boolean;
    };
    templateId?: string;
    designPersonalizationContextId?: string;
    dpcs?: any[];
    deliveryMode?: string;
    culture?: string;
    productId?: string;
    productVersion?: number;
    productOptions?: Record<string, string>;
    sourceProductId?: string;
    sourceProductVersion?: number;
    sourceProductOptions?: Record<string, string>;
    resize?: boolean;
}): string | null => {
    if (!documentUrl && !templateId) {
        return null;
    }

    const basePath = endpoint || `/api/${version}/composition:compose`;

    const params: Record<string, string | undefined> = {
        documentUrl: documentUrl ? (getUrl(documentUrl) ?? '') : undefined,
        assets: assets ? JSON.stringify(assets) : undefined,
        requester: REQUESTER,
        apiKey,
        priority,
        surfaceSpecificationUrl: surfaceSpecificationUrl || undefined,
        surfaceSpecification: surfaceSpecification ? JSON.stringify(surfaceSpecification) : undefined,
        applyOnlyToPlaceholders: applyOnlyToPlaceholders.toString(),
        smartCrop: smartCrop.toString(),
        featureFlags: JSON.stringify({
            enableMulticolorLogoContrastFix,
            enableMultiColorLogoToMonochromeConversion,
            enableLogoOutlineContrastFix,
            enableMulticolorLogoContrastBackgroundAdjustment,
            enableMulticolorLogoContrastBackgroundAdjustmentImprovement,
            enableMulticolorLogoContrastBackgroundAdjustmentDarkShades,
        }),
        fillDefaultText: fillDefaultText.toString(),
        brandProfileUrl: brandProfileUrl || undefined,
        templateId,
        designPersonalizationContextId,
        deliveryMode,
        culture,
        productId,
        productVersion: productVersion !== undefined ? productVersion.toString() : undefined,
        productOptions: productOptions ? JSON.stringify(productOptions) : undefined,
        sourceProductId,
        sourceProductVersion: sourceProductVersion !== undefined ? sourceProductVersion.toString() : undefined,
        sourceProductOptions: sourceProductOptions ? JSON.stringify(sourceProductOptions) : undefined,
        resize: resize !== undefined ? resize.toString() : undefined,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, hostUrl);

    // Append dpcs as repeated query params (dpcs=id1&dpcs=id2) to match backend expectation
    if (dpcs && dpcs.length > 0) {
        dpcs.forEach((dpc) => {
            url.searchParams.append('dpcs', typeof dpc === 'string' ? dpc : JSON.stringify(dpc));
        });
    }

    return url.toString();
};

export const getComposeDesignUrl = ({
    templateId,
    productId,
    productVersion,
    productOptions,
    sourceDocumentUrl,
    resize = false,
    designPersonalizationContextId,
    dpcs,
    deliveryMode = 'edit',
    culture,
    experimentFlags,
    apiKey,
    version = 'v3',
    hostUrl = host,
}: {
    templateId?: string;
    productId?: string;
    productVersion?: number;
    productOptions?: Record<string, string>;
    sourceDocumentUrl?: string;
    resize?: boolean;
    designPersonalizationContextId?: string;
    dpcs?: any[];
    deliveryMode?: string;
    culture?: string;
    experimentFlags?: Record<string, boolean>;
    apiKey?: string;
    version?: string;
    hostUrl?: string;
}): string | null => {
    if (!templateId) {
        return null;
    }

    const basePath = `/api/${version}/composeDesign`;

    const params: Record<string, string | undefined> = {
        templateId,
        productId,
        productVersion: productVersion !== undefined ? productVersion.toString() : undefined,
        productOptions: productOptions ? JSON.stringify(productOptions) : undefined,
        sourceDocumentUrl,
        resize: resize ? resize.toString() : undefined,
        designPersonalizationContextId,
        deliveryMode,
        culture,
        experimentFlags: experimentFlags ? JSON.stringify(experimentFlags) : undefined,
        apiKey,
        requester: REQUESTER,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, hostUrl);

    if (dpcs && dpcs.length > 0) {
        dpcs.forEach((dpc) => {
            url.searchParams.append('dpcs', typeof dpc === 'string' ? dpc : JSON.stringify(dpc));
        });
    }

    return url.toString();
};

export const getComposeDynamicLineUrl = ({
    dynamicLineId,
    useCaseId,
    colorVariantId,
    assets,
    apiKey,
    culture = 'en-US',
    deliveryMode = 'preview',
    surfaceSpecificationUrl,
    surfaceSpecification,
    productId,
    productVersion,
    productOptions,
    designPersonalizationContextId,
    dpcs,
    version = 'v3',
}: {
    dynamicLineId: string | null;
    useCaseId?: string;
    colorVariantId?: string;
    assets: ComposeAssets | null;
    apiKey: string;
    culture?: string;
    deliveryMode?: string;
    surfaceSpecificationUrl?: string;
    surfaceSpecification?: any;
    productId?: string;
    productVersion?: number;
    productOptions?: Record<string, string>;
    designPersonalizationContextId?: string;
    dpcs?: (DesignPersonalizationContext | string)[];
    version?: string;
}): string | null => {
    if (!dynamicLineId) {
        return null;
    }

    const basePath = `/api/${version}/composeDynamicLine`;

    const params: Record<string, string | undefined> = {
        dynamicLineId,
        useCaseId,
        colorVariantId,
        deliveryMode,
        apiKey,
        culture,
        assets: assets ? JSON.stringify(assets) : undefined,
        surfaceSpecificationUrl: surfaceSpecificationUrl || undefined,
        surfaceSpecification: surfaceSpecification ? JSON.stringify(surfaceSpecification) : undefined,
        requester: REQUESTER,
        productId,
        productVersion: productVersion !== undefined ? productVersion.toString() : undefined,
        productOptions: productOptions ? JSON.stringify(productOptions) : undefined,
        designPersonalizationContextId,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    // Append dpcs as repeated query params (dpcs=id1&dpcs=object) to match backend expectation
    if (dpcs && dpcs.length > 0) {
        dpcs.forEach((dpc) => {
            url.searchParams.append('dpcs', typeof dpc === 'string' ? dpc : JSON.stringify(dpc));
        });
    }

    return url.toString();
};

export const getEndpointUrl = ({
    version = 'v3',
    apiKey,
    hostUrl = host,
    endpoint,
}: {
    version?: string;
    apiKey: string;
    hostUrl?: string;
    endpoint?: string;
}): string => {
    const basePath = `/api/${version}/${endpoint}`;
    const url = new URL(basePath, hostUrl);
    url.searchParams.set('apiKey', apiKey);
    return url.toString();
};

export const getCompositionGenerateUrl = ({
    assets,
    surfaceSpecificationUrl,
    surfaceSpecification,
    sourceDocumentUrl,
    productName,
    brandProfileUrl,
    panels,
    dpiThreshold,
    apiKey,
    culture = 'en-us',
    substrateColor,
    version = 'v3',
    style,
    priority = 'quality',
    enableMulticolorLogoContrastFix = false,
    enableMulticolorLogoOutlineContrastFix = false,
    enableEmbroideryLogoContrastFix = false,
    enableGeneratePatternDocument = false,
}: {
    assets?: GenerateAssets;
    surfaceSpecificationUrl?: string;
    surfaceSpecification?: any;
    sourceDocumentUrl?: string;
    productName?: string;
    brandProfileUrl?: string;
    panels?: string;
    dpiThreshold?: number;
    apiKey: string;
    culture?: string;
    substrateColor?: string;
    version?: string;
    style?: string;
    priority?: string;
    enableMulticolorLogoContrastFix?: boolean;
    enableMulticolorLogoOutlineContrastFix?: boolean;
    enableEmbroideryLogoContrastFix?: boolean;
    enableGeneratePatternDocument?: boolean;
}): string => {
    const basePath = `/api/${version}/composition:generate`;

    const params: Record<string, string | undefined> = {
        surfaceSpecificationUrl: surfaceSpecificationUrl || undefined,
        surfaceSpecification: surfaceSpecification ? JSON.stringify(surfaceSpecification) : undefined,
        productName,
        culture,
        panels,
        dpiThreshold: dpiThreshold?.toString(),
        apiKey,
        waitTime: '30',
        sourceDocumentUrl: sourceDocumentUrl || undefined,
        brandProfileUrl: brandProfileUrl || undefined,
        substrateColor: substrateColor || undefined,
        assets: assets ? JSON.stringify(assets) : undefined,
        style: style || undefined,
        ...(version === 'v4' && { priority }),
        ...(enableMulticolorLogoContrastFix && { enableMulticolorLogoContrastFix: 'true' }),
        ...(enableMulticolorLogoOutlineContrastFix && {
            enableMulticolorLogoOutlineContrastFix: 'true',
        }),
        ...(enableEmbroideryLogoContrastFix && {
            enableEmbroideryLogoContrastFix: 'true',
        }),
        ...(enableGeneratePatternDocument && {
            enableGeneratePatternDocument: 'true',
        }),
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);
    return url.toString();
};

type GetInspirationGenerateUrlsInput = {
    themeQuery: string | null;
    iconCollection: string | null;
    texts: any | null;
    imageAsset?: string;
    surfaceSpecificationUrl: string | null;
    surfaceSpecification: any | null;
    substrateColor: string | null;
    useImage: boolean | null;
    colorPalette: any | null;
    colorFilter?: any;
    contentAlignment?: string;
    signal?: AbortSignal;
    apiKey: string;
    brandProfileUrl?: string;
} & ({ version: 'v3' } | { version: 'v4'; filters: InspirationGenerateV4Filters });

export const getInspirationGenerateUrls = async (input: GetInspirationGenerateUrlsInput): Promise<string[]> => {
    const {
        themeQuery,
        imageAsset,
        texts,
        surfaceSpecificationUrl,
        surfaceSpecification,
        iconCollection,
        substrateColor,
        useImage,
        colorPalette,
        contentAlignment,
        signal,
        apiKey,
        brandProfileUrl,
    } = input;

    const basePath = `/api/${input.version}/inspiration:generate`;
    const requestBody: Record<string, any> = {
        surfaceSpecificationUrl: surfaceSpecificationUrl ?? undefined,
        surfaceSpecification,
        texts,
        imageAsset: imageAsset ? (typeof imageAsset === 'string' ? JSON.parse(imageAsset) : imageAsset) : undefined,
        substrateColor: substrateColor ?? undefined,
        useImage: useImage !== null ? useImage : undefined,
        themeQuery: themeQuery ?? undefined,
        colorPalette: colorPalette ?? undefined,
        apiKey: apiKey,
        brandProfileUrl: brandProfileUrl ?? undefined,
    };

    if (input.version === 'v4' && 'filters' in input) {
        requestBody.filters = input.filters;
    }

    if (iconCollection || contentAlignment) {
        const generateOptionsObj: InspirationGenerateOptions = {};
        if (iconCollection) generateOptionsObj.iconCollections = [iconCollection];
        if (contentAlignment) generateOptionsObj.contentAlignment = contentAlignment;
        requestBody.generateOptions = generateOptionsObj;
    }

    try {
        const response = await axios.post<CimpressDocumentV2[]>(`${host}${basePath}`, requestBody, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            validateStatus: (status) => status >= 200 && status < 300,
        });

        const responseData = response.data;
        const resultDocumentUrls = await Promise.all(
            responseData.map(async (document: any) => {
                try {
                    const url = getTransientDocumentUrl(document);
                    return url;
                } catch (error) {
                    document.deleteAfterDays = 1;
                    return await uploadJsonDocumentAndGetDocumentUrl(document, signal);
                }
            }),
        );
        return resultDocumentUrls;
    } catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error(`Error getting inspiration generate urls: ${error}`);
    }
};

const createUrlWithParams = (basePath: string, params: Record<string, string | undefined>, host: string): URL => {
    const url = new URL(basePath, host);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
            url.searchParams.set(key, String(value));
        }
    });

    return url;
};

export async function getCimpressDocumentV2(documentUrl: string, signal?: AbortSignal) {
    const headers = await getAuthHeaders();
    const { data } = await axios.get<CimpressDocumentV2>(documentUrl, { signal, headers });
    return data;
}

export function getTransientDocumentUrl(cimdoc: CimpressDocumentV2, fromUds?: boolean) {
    const MAX_DATA_URI_LENGTH = 7000;

    const deflatedDocument = pako.deflateRaw(JSON.stringify(cimdoc), {
        to: 'string',
    } as any); // For some reason we're passing an object that doesn't match the types

    const transientDocument = base64js.fromByteArray(deflatedDocument);

    if (transientDocument.length > MAX_DATA_URI_LENGTH) {
        throw new Error(`Provided cimdoc exceeds inline document url length of ${MAX_DATA_URI_LENGTH}`);
    }

    if (fromUds) {
        const url = createUrlWithParams(
            '/v3/instructions:preview',
            {
                documentUri: transientDocument,
            },
            'https://uds.documents.cimpress.io',
        );
        return url.toString();
    }

    const url = createUrlWithParamsAndNonce(
        '/v3/documents/transient',
        {
            document: transientDocument,
        },
        'https://storage.documents.cimpress.io',
    );

    return url.toString();
}

export const getRenderingUrlDocument = (documentUrl: string, panel: number, backgroundColor?: string): string => {
    const basePath = '/v3/instructions:preview';

    const params: Record<string, string | undefined> = {
        documentUri: documentUrl,
        surfaceOrdinals: `${panel + 1}`,
        ignoreProjection: 'True',
    };

    const instructionsUri = createUrlWithParamsAndNonce(
        basePath,
        params,
        'https://instructions.documents.cimpress.io',
    ).toString();

    let renderingUrl = `https://rendering.documents.cimpress.io/v2/orchestration/preview?width=700&height=700&instructions_uri=${encodeURIComponent(instructionsUri)}&format=png`;

    if (backgroundColor) {
        renderingUrl += `&bgColor=${parseBackgroundColor(backgroundColor)}`;
    }

    return renderingUrl;
};

export const getRenderingUrl = (
    documentUrl: string,
    panel: number,
    surfaceSpecUrl: string,
    projection: string,
    backgroundColor: string | undefined,
): string => {
    const isSurfaceUrlDSS = surfaceSpecUrl.includes('design-specifications');

    const sceneUriPart = isSurfaceUrlDSS ? '' : createSceneUriPart(panel, projection, surfaceSpecUrl);
    const instructionsUriPart = createInstructionUriPart(documentUrl);

    let renderingUrl = `https://rendering.documents.cimpress.io/v1/cse/preview?${instructionsUriPart}&showerr=1&width=1360&format=png`;
    if (sceneUriPart) {
        renderingUrl += `&scene=${sceneUriPart}`;
    }
    if (backgroundColor) {
        renderingUrl += `&bgColor=${parseBackgroundColor(backgroundColor)}`;
    }
    return renderingUrl;
};

export const getRenderingUrlWithPhotoScene = (documentUrl: string, scene: string | null): string => {
    const instructionsUriPart = createInstructionUriPart(documentUrl);
    const encodedScene = scene ? encodeURIComponent(scene) : '';
    let renderingUrl = `https://rendering.documents.cimpress.io/v1/cse/preview?${instructionsUriPart}&showerr=1&width=1360&format=png`;
    if (encodedScene) {
        renderingUrl += `&scene=${encodedScene}`;
    }

    return renderingUrl;
};

function createInstructionUriPart(documentUrl: string): string {
    const docInstructionsUrl = `https://instructions.documents.cimpress.io/v3/instructions:preview?documentUri=${encodeURIComponent(
        documentUrl,
    )}&salt=${Math.random()}`;

    const instructionsUriPart = docInstructionsUrl ? `instructions_uri=${encodeURIComponent(docInstructionsUrl)}` : '';

    return instructionsUriPart;
}

function createSceneUriPart(panel: number, projection: string, surfaceSpecUrl: string): string {
    const sceneJson = {
        width: 1000,
        page: panel + 1,
        projection: projection,
        // showFullBleed: true,
        product: {
            calculatedSurfaceSetUrl: surfaceSpecUrl,
        },
        layers: [
            {
                type: 'overlay',
                source: 'safe',
                stroke: {
                    color: 'rgb(0,0,0)',
                    dotted: true,
                },
            },
        ],
    };

    // Deflate the scene
    const deflatedScene = pako.deflateRaw(JSON.stringify(sceneJson), {
        to: 'string',
    } as any); // For some reason we're passing an object that doesn't match the types

    const transientScene = base64js.fromByteArray(deflatedScene);

    // Construct the scene Url
    const sceneUrl = `https://scenes.documents.cimpress.io/v3/transient?data=${encodeURIComponent(transientScene)}`;
    const sceneUriPart = `${encodeURIComponent(sceneUrl)}`;

    return sceneUriPart;
}

export const postGenerateImage = async ({
    body,
    signal,
}: {
    body: {
        prompt: string;
        model?: string;
        mediaType?: string;
        contentType?: string;
        aspectRatio?: string;
    };
    signal: AbortSignal;
}): Promise<string> => {
    const url = `${host}/api/v4/inspiration:generateImage`;

    try {
        const headers = await getAuthHeaders();
        const response = await axios.post(url, body, { signal, headers });
        return response.data.imageUrl;
    } catch (error) {
        console.error('Error in fetching image URLs:', error);
        throw error;
    }
};

export const getCimdocEnrichmentUrl = async (
    fileUrl: string,
    previewImageUrl: string,
    type: CustomInputType,
    transformSpace?: 'raster' | 'vector',
): Promise<string | null> => {
    if (!fileUrl) {
        return null;
    }

    const documentUrl =
        type !== 'document'
            ? await createCimdocWithImage(fileUrl, previewImageUrl, new AbortController().signal)
            : fileUrl;
    const basePath = `/api/v4/quality:cimdocEnrichment`;

    const params: Record<string, string> = {
        documentUrl,
        ...(transformSpace !== undefined && { transformSpace }),
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    return url.toString();
};

export const getAdaptToProductUrl = (
    sourceProductName: string,
    sourceDocumentUrl: string,
    targetProductName: string,
    targetSurfaceSpecificationUrl: string,
    apiKey: string,
    sourceSurfaceSpecificationUrl?: string,
    targetSubstrateColor?: string,
    culture?: string,
    useTemplateMatching?: boolean,
    useCamDiscoveryTemplateMatching?: boolean,
): string | null => {
    if (!sourceProductName || !sourceDocumentUrl || !targetProductName || !targetSurfaceSpecificationUrl) {
        return null;
    }

    const basePath = `/api/v3/adaptation:adaptToProduct`;

    const params: Record<string, string | undefined> = {
        sourceProductName,
        sourceDocumentUrl,
        targetProductName,
        targetSurfaceSpecificationUrl,
        apiKey,
        ...(sourceSurfaceSpecificationUrl && { sourceSurfaceSpecificationUrl }),
        ...(targetSubstrateColor && { targetSubstrateColor }),
        ...(culture && { culture }),
        useTemplateMatching: useTemplateMatching?.toString(),
        useCamDiscoveryTemplateMatching: useCamDiscoveryTemplateMatching?.toString(),
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);
    return url.toString();
};

export interface AdaptToLayoutTemplate {
    templateUrl?: string;
    templateJson?: unknown;
}

interface PostAdaptToLayoutInput {
    documentUrl: string;
    surfaceSpecificationUrl: string;
    layout: string;
    apiKey: string;
    signal: AbortSignal;
    template?: AdaptToLayoutTemplate;
    minimumFontSize?: string;
}

interface AdaptToLayoutResponse {
    documentUrl?: string;
}

const isCimpressDocumentV2Response = (
    value: CimpressDocumentV2 | AdaptToLayoutResponse,
): value is CimpressDocumentV2 => {
    return 'document' in value && 'version' in value;
};

export const postAdaptToLayoutDocumentUrl = async ({
    documentUrl,
    surfaceSpecificationUrl,
    layout,
    apiKey,
    signal,
    template,
    minimumFontSize,
}: PostAdaptToLayoutInput): Promise<string> => {
    const url = `${host}/api/v4/adaptation:adaptToLayout`;

    const requestBody: {
        documentUrl: string;
        surfaceSpecificationUrl: string;
        layout: string;
        apiKey: string;
        minimumFontSize?: string;
        templateUrl?: string;
        template?: unknown;
    } = {
        documentUrl,
        surfaceSpecificationUrl,
        layout,
        apiKey,
        minimumFontSize,
        ...(template?.templateUrl ? { templateUrl: template.templateUrl } : {}),
        ...(template?.templateJson ? { template: template.templateJson } : {}),
    };

    try {
        const headers = await getAuthHeaders();
        const response = await axios.post<CimpressDocumentV2 | AdaptToLayoutResponse>(url, requestBody, {
            signal,
            headers,
        });

        if (!response.data) {
            throw new Error('Empty response from API');
        }

        if ('documentUrl' in response.data && typeof response.data.documentUrl === 'string') {
            return response.data.documentUrl;
        }

        if (!isCimpressDocumentV2Response(response.data)) {
            throw new Error('API response missing documentUrl and Cimpress document payload');
        }

        const document = response.data;

        try {
            return getTransientDocumentUrl(document);
        } catch (error) {
            document.deleteAfterDays = 1;
            return await uploadJsonDocumentAndGetDocumentUrl(document, signal);
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('API Error:', error.response?.data || error.message);
            throw new Error(`API request failed: ${error.response?.data?.message || error.message}`);
        }

        console.error('Error adapting layout:', error);
        throw error;
    }
};

export const getAutoLayoutUrl = ({
    conceptKitId,
    useCaseId,
    colorVariantId,
    productId,
    productVersion,
    culture = 'en-US',
    designPersonalizationContextId,
    dpcs,
    layoutId,
    productOptions,
    substrateColor,
    deliveryMode,
    assets,
    apiKey = 'doUnpmod9tDLH2ztFqyGyVDKNg7QLEaDpAs',
}: {
    conceptKitId: string;
    useCaseId: string;
    colorVariantId?: string;
    productId: string;
    productVersion: string;
    culture?: string;
    designPersonalizationContextId?: string;
    dpcs?: any[];
    layoutId?: string;
    productOptions?: Record<string, string>;
    substrateColor?: string;
    deliveryMode?: string;
    assets?: ComposeAssets;
    apiKey?: string;
}): string => {
    const basePath = '/api/v3/autoLayout';

    const params: Record<string, string | undefined> = {
        conceptKitId,
        useCaseId,
        colorVariantId,
        productId,
        productVersion,
        culture,
        designPersonalizationContextId,
        layoutId,
        productOptions: productOptions ? JSON.stringify(productOptions) : undefined,
        substrateColor,
        deliveryMode,
        assets: assets ? JSON.stringify(assets) : undefined,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    // Append dpcs as repeated query params (dpcs=id1&dpcs=object) to match backend expectation
    if (dpcs && dpcs.length > 0) {
        dpcs.forEach((dpc) => {
            url.searchParams.append('dpcs', typeof dpc === 'string' ? dpc : JSON.stringify(dpc));
        });
    }

    return url.toString();
};

export const getConvertRasterToVector = async ({
    imageUrl,
    signal,
    apiKey,
}: {
    imageUrl: string;
    signal: AbortSignal;
    apiKey: string;
}): Promise<string> => {
    const basePath = '/api/v3/quality:convertRasterToVector';
    const params: Record<string, string | undefined> = {
        imageUrl,
        apiKey,
    };

    const url = createUrlWithParamsAndNonce(basePath, params, host);

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(url.toString(), {
            method: 'GET',
            signal,
            headers,
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(`Error fetching result: ${result.message}`);
        }

        return result.imageUrl;
    } catch (error) {
        console.error('Error in fetching result URL:', error);
        throw error;
    }
};

const createUrlWithParamsAndNonce = (
    basePath: string,
    params: Record<string, string | undefined>,
    host: string,
): URL => {
    const url = new URL(basePath, host);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
            url.searchParams.set(key, String(value));
        }
    });

    url.searchParams.set('nonce', uuidv4());

    return url;
};

export async function getDocumentUrlFromAgUrl(agUrl: string, signal?: AbortSignal): Promise<string> {
    let documentResponse = await axios.get<CimpressDocumentV2>(agUrl, { signal });
    if (documentResponse.status === HttpStatusCode.UriTooLong) {
        const agsBody = convertGetRequestToPostRequest(agUrl);
        documentResponse = await axios.post<CimpressDocumentV2>(agUrl, agsBody, { signal });
    }
    if (documentResponse.status !== HttpStatusCode.Ok) {
        throw new Error(`Error fetching document: ${documentResponse.statusText}`);
    }

    const document = documentResponse.data;
    try {
        const url = getTransientDocumentUrl(document);
        return url;
    } catch (error) {
        document.deleteAfterDays = 1;
        return await uploadJsonDocumentAndGetDocumentUrl(document, signal);
    }
}

function convertGetRequestToPostRequest(agUrl: string): { agUrl: string; agBody: object } {
    const url = new URL(agUrl);
    const params = url.searchParams;
    const body = Object.fromEntries(params);
    const agBody: Record<string, any> = {};

    for (const [key, value] of Object.entries(body)) {
        agBody[key] = value;
        if (value === 'true') {
            agBody[key] = true;
        } else if (value === 'false') {
            agBody[key] = false;
        } else if (!Number.isNaN(Number(value))) {
            agBody[key] = Number(value);
        } else if (typeof value === 'string') {
            try {
                agBody[key] = JSON.parse(value);
            } catch (error) {
                agBody[key] = value;
            }
        }
        url.searchParams.delete(key);
    }
    return { agUrl: url.toString(), agBody };
}

function isValidUrl(value: any): boolean {
    if (typeof value !== 'string' || !value.trim()) {
        return false;
    }

    // Check for common URL patterns
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('file://')) {
        return true;
    }

    // Check for relative URLs
    if (value.startsWith('/') || value.startsWith('//')) {
        return true;
    }

    // Check for protocol-less URLs (like //example.com)
    if (value.startsWith('//')) {
        return true;
    }

    return false;
}

export function getResizeDocumentWithSurface(
    documentUrl: string,
    surfaceSpecification: string | any,
    minimumFontSize: string | undefined,
    apiKey: string,
    priority: string = 'quality',
    sourceSurfaceSpecification?: string | any,
    version: string = 'v3',
): string {
    const basePath = `/api/${version}/adaptation:resize`;

    const params: Record<string, string | undefined> = {
        documentUrl,
        requester: REQUESTER,
        apiKey,
        minimumFontSize,
        priority,
    };

    // Handle surface specification
    if (isValidUrl(surfaceSpecification)) {
        params.surfaceSpecificationUrl = surfaceSpecification;
    } else {
        // If it's already a string, don't stringify again
        if (typeof surfaceSpecification === 'string') {
            params.surfaceSpecification = surfaceSpecification;
        } else {
            params.surfaceSpecification = JSON.stringify(surfaceSpecification);
        }
    }

    // Handle source surface specification
    if (sourceSurfaceSpecification) {
        if (isValidUrl(sourceSurfaceSpecification)) {
            params.sourceSurfaceSpecificationUrl = sourceSurfaceSpecification;
        } else {
            // If it's already a string, don't stringify again
            if (typeof sourceSurfaceSpecification === 'string') {
                params.sourceSurfaceSpecification = sourceSurfaceSpecification;
            } else {
                params.sourceSurfaceSpecification = JSON.stringify(sourceSurfaceSpecification);
            }
        }
    }

    const url = createUrlWithParamsAndNonce(basePath, params, host);
    return url.toString();
}

function parseBackgroundColor(backgroundColor: string): string {
    if (backgroundColor.startsWith('#')) {
        return backgroundColor.slice(1);
    }
    return backgroundColor;
}

export type CustomInputType = 'image' | 'pdf' | 'document' | 'unknown';

export async function getTypeOfContentUnderUrl(url: string): Promise<CustomInputType> {
    try {
        const response = await axios.get(url, { method: 'HEAD' });
        if (response.status === 200) {
            const contentType = response.headers['content-type'];
            if (contentType) {
                if (contentType.includes('image/')) {
                    return 'image';
                } else if (contentType.includes('application/pdf')) {
                    return 'pdf';
                } else if (contentType.includes('application/json')) {
                    return 'document';
                }
            }
        }
        return 'unknown';
    } catch (error) {
        return 'unknown';
    }
}

export function getPreviewUrlForPdf(pdfUrl: string): string {
    if (pdfUrl.includes('sherbert')) {
        const url = new URL(pdfUrl);
        url.pathname += '/webPreview';
        return url.toString();
    }
    const url = new URL(pdfUrl);
    url.searchParams.set('variant', 'preview');
    return url.toString();
}

/**
 * Parses a DSS (Design Specifications Service) URL to extract product information
 * for use with the composeDynamicLine API.
 *
 * @param dssUrl - The DSS URL (e.g., https://design-specifications.docext.cimpress.io/v2/surfaces/products/PRD-XXX/51?...)
 * @returns Object containing productId, productVersion, and productOptions extracted from the URL
 *
 * @example
 * const dssUrl = 'https://design-specifications.docext.cimpress.io/v2/surfaces/products/PRD-R1AJEBVL/51?culture=en-us&optionSelections[Orientation]=Horizontal&optionSelections[Size]=18" x 5"';
 * const result = parseDssUrl(dssUrl);
 * // Returns:
 * // {
 * //   productId: 'PRD-R1AJEBVL',
 * //   productVersion: 51,
 * //   productOptions: { Orientation: 'Horizontal', Size: '18" x 5"' }
 * // }
 */
export function parseDssUrl(dssUrl: string): {
    productId: string;
    productVersion: number;
    productOptions: Record<string, string>;
} {
    const url = new URL(dssUrl);
    const pathSegments = url.pathname.split('/').filter(Boolean);

    // Extract productId and productVersion from path
    // Path structure: /v2/surfaces/products/{productId}/{productVersion}
    const productId = pathSegments[3]; // Index 3 is productId
    const productVersion = parseInt(pathSegments[4], 10); // Index 4 is productVersion

    // Extract productOptions from optionSelections query params
    const productOptions: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
        // Convert optionSelections[key] to productOptions
        const optionMatch = key.match(/^optionSelections\[(.+)\]$/);
        if (optionMatch) {
            const optionKey = optionMatch[1];
            productOptions[optionKey] = value;
        }
    });

    return {
        productId,
        productVersion,
        productOptions,
    };
}
